import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');
  const [listing_id, setListingId] = useState('');

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/deposit/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setListingId(data.listing_id);
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => setAttempts(a => a + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => setAttempts(a => a + 1), 2000);
    }
  }, [attempts, sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, attempts, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-xl mx-auto px-6 text-center">
          {status === 'checking' && (
            <div className="py-20">
              <Loader2 className="h-16 w-16 animate-spin text-accent mx-auto mb-6" />
              <h1 className="font-outfit text-3xl font-semibold mb-4">
                Confirming Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we verify your payment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-20" data-testid="payment-success">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="font-outfit text-3xl font-semibold mb-4">
                Payment Successful!
              </h1>
              <p className="text-muted-foreground mb-8">
                Your deposit has been received. We're now dispatching your transport request to our carrier network.
              </p>
              <div className="space-y-4">
                <p className="text-sm">
                  <span className="text-muted-foreground"> Here is your Listing ID: </span>
                  <span className="font-mono">{listing_id}</span>
                </p>
              </div>
              <div className="mt-8 space-y-3">
                <p className="text-sm text-muted-foreground">
                 
                  You'll receive SMS and email updates as we find the best carrier for your Tesla.
                </p>
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-primary text-primary-foreground rounded-full px-8"
                >
                  Return Home
                </Button>
              </div>
            </div>
          )}

          {status === 'expired' && (
            <div className="py-20">
              <h1 className="font-outfit text-3xl font-semibold mb-4 text-destructive">
                Session Expired
              </h1>
              <p className="text-muted-foreground mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Button 
                onClick={() => navigate('/quote')}
                className="bg-primary text-primary-foreground rounded-full px-8"
              >
                Get New Quote
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="py-20">
              <h1 className="font-outfit text-3xl font-semibold mb-4">
                Verification Timeout
              </h1>
              <p className="text-muted-foreground mb-8">
                We're still verifying your payment. You'll receive an email confirmation shortly.
              </p>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="rounded-full px-8"
              >
                Return Home
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
